/**
 * Plain-DOM user tag for grid cells — zero React per cell.
 *
 * Grid cells used to render <UserNameTagComponent> through a React root (later a
 * portal) per cell. Either way React attaches its full delegated event set to
 * every cell container, and each cell held its own Redux subscription — with a
 * user column in every grid (and several per row in mailbox) userTag was the
 * dominant scroll/idle lag. This module renders the same visuals with no React,
 * no MUI and no per-cell store subscription:
 *
 *  - name/avatar resolve synchronously from store.getState().userTagData
 *  - cells whose user isn't loaded yet register in a module-level Map and ONE
 *    store.subscribe patches them when data lands (the per-dispatch cost is a
 *    single slice identity check, total — not one callback per cell)
 *  - clicks dispatch openUserProfile / openGroupedUsersModal; the actual modals
 *    are mounted once at app level (UserProfileModalHost), never in the cell
 *  - the delegated gridCellTooltip shows the full name on overflow; the chip
 *    exposes it via `data-cube-tip` so the tooltip reads the name alone — plain
 *    textContent would concatenate the avatar initials ("ASarun Sharma")
 *
 * Visuals mirror UserNameTagComponentWithProvided.tsx (MUI Chip size="medium"
 * with avatar, and MUI AvatarGroup for grouped users) — keep them in sync.
 */
import { store } from "../../ProviderApp";
import { fetchUserTagData } from "../../actions/setUserTagData";
import { getNameInitials } from "../constants/globalFunctions";
import { openUserProfile, openGroupedUsersModal, openExternalUser } from "../../reducers/userProfileModal";
import type { UserTagState } from "../../reducers/userTagData";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";

export interface RenderUserTagOptions {
  /** Account id to resolve; also enables the click → profile-modal behaviour.
   *  Absent when there is no account behind the tag (external/email-only user). */
  id?: ObjectIdString;
  /** Fallback label (and initials source) shown until — or instead of — resolved data */
  fullName?: string;
  /** Email of an external (no-`id`) user. When set and there is no `id`, clicking
   *  the chip opens the external-user profile modal (name + email). */
  email?: string;
  /** When false the chip attaches no click handler of its own, so a click bubbles to an
   *  ancestor handler instead (e.g. renderUserTagList's grouped-users modal). Default true. */
  interactive?: boolean;
  /** Called with the chip's visible label whenever it changes — the fallback first (if any), then the
   *  store-resolved name once userTagData lands. renderUserTagList uses it to keep its
   *  comma-separated cell tooltip in sync with names it was never handed. */
  onLabel?: (label: string) => void;
}

export type UserTagAvatarSize = "XXS" | "XS" | "LSM" | "SM" | "LG" | "LLG" | "XL" | "GR";

export interface RenderUserTagGroupOptions {
  ids: ObjectIdString[];
  /** MUI AvatarGroup `max` semantics: above this, max-1 avatars + a "+N" surplus chip */
  maxAvatars?: number;
  size?: UserTagAvatarSize;
  /** When true, clicking the group opens the grouped-users details modal */
  enableDetailsModal?: boolean;
  detailsModalHeader?: string;
}

export interface RenderUserTagListOptions {
  /** One full chip is rendered per entry, in order. Same shape as RenderUserTagOptions. */
  users: RenderUserTagOptions[];
  /** When true, a click anywhere on the row opens the grouped-users details modal, and the
   *  individual chips stop opening their own profile modals so the whole cell behaves as one. */
  enableDetailsModal?: boolean;
  detailsModalHeader?: string;
}

// Mirrors AvatarComponent.getSizeClass
const AVATAR_SIZE_REM: Record<UserTagAvatarSize, number> = {
  XXS: 1.25,
  XS: 1.5,
  LSM: 1.875,
  SM: 2.25,
  LG: 3,
  LLG: 4.6875,
  XL: 6.25,
  GR: 2.5,
};

/* ------------------------------------------------------------------
    Styles (injected once)
------------------------------------------------------------------ */

let cssInstalled = false;

function ensureCss(): void {
  if (cssInstalled || typeof document === "undefined") return;
  cssInstalled = true;
  const style = document.createElement("style");
  style.id = "cube-usertag-css";
  // Metrics copied from MUI Chip (size="medium", with avatar) and MUI
  // AvatarGroup so plain-DOM cells are indistinguishable from the React tag.
  style.textContent = `
.cube-usertag {
  max-width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 2rem;
  box-sizing: border-box;
  font-family: inherit;
  font-size: 0.8125rem;
  line-height: normal;
  color: var(--primary-text);
  background-color: rgba(0, 0, 0, 0.08);
  border-radius: 1rem;
  white-space: nowrap;
  vertical-align: middle;
  cursor: pointer;
  user-select: none;
}
.cube-usertag:hover {
  background-color: rgba(0, 0, 0, 0.12);
}
.cube-usertag__avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 1.5rem;
  height: 1.5rem;
  margin-left: 0.3125rem;
  margin-right: -0.375rem;
  border-radius: 50%;
  overflow: hidden;
  background-color: #bdbdbd;
  color: #fff;
  font-size: 0.75rem;
  line-height: 1;
}
.cube-usertag__avatar > img,
.cube-usertag-group__avatar > img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.cube-usertag__label {
  min-width: 0; /* let the flex item shrink so long names ellipsize here instead of overflowing the chip */
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding-left: 0.75rem;
  padding-right: 0.75rem;
}
.cube-usertag-list {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  max-width: 100%;
  overflow: hidden;
}
/* Chips keep their natural width and the row clips at the cell edge (no shrink /
   no wrap) so each assignee reads at full size like a standalone user tag. */
.cube-usertag-list > .cube-usertag {
  flex-shrink: 0;
}
.cube-usertag-list--clickable {
  cursor: pointer;
}
.cube-usertag-group {
  display: inline-flex;
  align-items: center;
}
.cube-usertag-group--clickable {
  cursor: pointer;
}
.cube-usertag-group__avatar {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-sizing: content-box;
  border: 0.125rem solid #fff;
  border-radius: 50%;
  overflow: hidden;
  background-color: var(--system-colour-2);
  color: #fff;
  line-height: 1;
  user-select: none;
}
.cube-usertag-group__avatar + .cube-usertag-group__avatar {
  margin-left: -0.5rem;
}
`;
  document.head.appendChild(style);
}

/* ------------------------------------------------------------------
    One store subscription for every rendered tag
------------------------------------------------------------------ */

interface TagBinding {
  id: string;
  kind: "chip" | "groupAvatar";
  /** Chip root (hidden until data/fallback) — or the group avatar circle itself */
  el: HTMLElement;
  avatarEl: HTMLElement;
  labelEl?: HTMLElement;
  fallbackName: string;
  createdAt: number;
  /** userTagData entry identity last painted — skips repaints on unrelated changes */
  painted: unknown;
  /** Notified with the resolved label on every paint (see RenderUserTagOptions.onLabel) */
  onLabel?: (label: string) => void;
}

// Containers younger than this may be mid-attach by SlickGrid — never swept.
// (Formatters build the cell DOM before the grid inserts it; see cellPortals.tsx.)
const GRACE_MS = 2000;

const bindingsById = new Map<string, Set<TagBinding>>();
let subscribed = false;
let lastSlice: UserTagState | undefined;
let patchScheduled = false;
let sweepScheduled = false;

const idle: (cb: () => void) => void =
  typeof requestIdleCallback === "function"
    ? (cb) => {
        requestIdleCallback(() => cb(), { timeout: 1000 });
      }
    : (cb) => {
        setTimeout(cb, 300);
      };

function ensureSubscription(): void {
  if (subscribed) return;
  subscribed = true;
  lastSlice = store.getState().userTagData;
  // Never unsubscribed — one module-level listener for the app's lifetime whose
  // per-dispatch cost is the identity check below.
  store.subscribe(() => {
    const slice = store.getState().userTagData;
    if (slice === lastSlice) return;
    lastSlice = slice;
    schedulePatch();
  });
}

function schedulePatch(): void {
  if (patchScheduled) return;
  patchScheduled = true;
  requestAnimationFrame(() => {
    patchScheduled = false;
    const slice = store.getState().userTagData;
    let sawDisconnected = false;
    for (const set of bindingsById.values()) {
      for (const binding of set) {
        if (!binding.el.isConnected) sawDisconnected = true;
        const entry = slice[binding.id];
        if (entry?.isLoaded && binding.painted !== entry) paint(binding, entry.data);
      }
    }
    if (sawDisconnected) scheduleSweep();
  });
}

/** Drop bindings whose element left the document (recycled grid cells). */
function scheduleSweep(): void {
  if (sweepScheduled) return;
  sweepScheduled = true;
  idle(() => {
    sweepScheduled = false;
    const now = Date.now();
    let remaining = false;
    for (const [id, set] of bindingsById) {
      for (const binding of set) {
        if (!binding.el.isConnected) {
          if (now - binding.createdAt > GRACE_MS) set.delete(binding);
          else remaining = true;
        }
      }
      if (!set.size) bindingsById.delete(id);
    }
    // keep sweeping while recycled containers remain (fling / navigation)
    if (!remaining) {
      outer: for (const set of bindingsById.values()) {
        for (const binding of set) {
          if (!binding.el.isConnected) {
            remaining = true;
            break outer;
          }
        }
      }
    }
    if (remaining) scheduleSweep();
  });
}

function register(binding: TagBinding): void {
  let set = bindingsById.get(binding.id);
  if (!set) {
    set = new Set();
    bindingsById.set(binding.id, set);
  }
  set.add(binding);
  ensureSubscription();
  scheduleSweep();
}

function requestUserData(id: ObjectIdString): void {
  store.dispatch(fetchUserTagData(id));
}

/* ------------------------------------------------------------------
    DOM building / painting
------------------------------------------------------------------ */

function initialsFrom(firstName?: string, lastName?: string): string {
  return getNameInitials({ firstName: firstName ?? "", lastName: lastName ?? "" }) || "";
}

function initialsFromFullName(fullName: string): string {
  const parts = fullName.split(" ");
  return initialsFrom(parts[0] || "", parts[1] || "");
}

function setAvatarImage(avatarEl: HTMLElement, src: string): void {
  avatarEl.textContent = "";
  const img = document.createElement("img");
  img.src = src;
  img.alt = "";
  avatarEl.appendChild(img);
}

function paint(binding: TagBinding, data: UserTagState[string]["data"]): void {
  if (!data) return;
  binding.painted = store.getState().userTagData[binding.id];
  if (data.profilePic) setAvatarImage(binding.avatarEl, data.profilePic);
  else binding.avatarEl.textContent = initialsFrom(data.firstName, data.lastName);
  // Matches the React chip's label choice: resolved fullName, else the fallback
  const label = data.fullName || binding.fallbackName;
  if (binding.kind === "chip") {
    if (binding.labelEl) binding.labelEl.textContent = label;
    // Tooltip text = the name only; without this the delegated tooltip falls back
    // to the chip's textContent, i.e. avatar initials + name ("ASarun Sharma").
    binding.el.setAttribute("data-cube-tip", label);
    binding.el.style.display = "";
  }
  // Last, so a caller that owns the tooltip for a whole row of chips (renderUserTagList) can
  // overwrite the per-chip tip set above with the full comma-separated list.
  binding.onLabel?.(label);
}

/**
 * Render a single-user tag (MUI-Chip look) into `container` and return it.
 * Drop-in for mounting <UserNameTagComponent id={...} fullName={...}/> in a
 * grid cell. Like the React version, nothing is visible until the user's data
 * is in the store unless `fullName` provides a fallback label.
 */
export function renderUserTag(
  container: HTMLElement,
  { id, fullName, email, interactive = true, onLabel }: RenderUserTagOptions,
): HTMLElement {
  ensureCss();

  const chip = document.createElement("div");
  chip.className = "cube-usertag";
  if (!interactive) chip.style.cursor = "inherit";
  const avatarEl = document.createElement("div");
  avatarEl.className = "cube-usertag__avatar";
  const labelEl = document.createElement("span");
  labelEl.className = "cube-usertag__label";
  chip.append(avatarEl, labelEl);

  // For an external (no-`id`) user we may only have the email — fall back to it so
  // the chip still renders and stays clickable.
  const fallbackLabel = fullName || (!id ? email : undefined);
  if (fallbackLabel) {
    labelEl.textContent = fallbackLabel;
    avatarEl.textContent = initialsFromFullName(fallbackLabel);
    chip.setAttribute("data-cube-tip", fallbackLabel);
  } else {
    chip.style.display = "none";
  }

  // stopPropagation mirrors the React chip: clicking a user tag never triggers
  // the grid's own cell/row click handling. Skipped when non-interactive so the
  // click reaches an ancestor handler (e.g. renderUserTagList's grouped modal).
  if (interactive) {
    chip.onclick = (e) => {
      e.stopPropagation();
      if (id) {
        const entry = store.getState().userTagData[id];
        if (entry?.isLoaded && entry.data?.fullName) store.dispatch(openUserProfile(id));
      } else if (email) {
        // External (email-sourced) user with no account: show name + email in the modal.
        store.dispatch(openExternalUser({ fullName, email }));
      }
    };
  }

  if (id) {
    const binding: TagBinding = {
      id,
      kind: "chip",
      el: chip,
      avatarEl,
      labelEl,
      fallbackName: fullName ?? "",
      createdAt: Date.now(),
      painted: undefined,
      onLabel,
    };
    const entry = store.getState().userTagData[id];
    if (entry?.isLoaded) paint(binding, entry.data);
    else requestUserData(id);
    register(binding);
  }

  container.appendChild(chip);
  return container;
}

/**
 * Render a grouped-users tag (MUI-AvatarGroup look) into `container` and
 * return it. Drop-in for mounting
 * <UserNameTagComponent userIdsForGroup={...} maxAvatars={...}/> in a cell.
 */
export function renderUserTagGroup(
  container: HTMLElement,
  { ids, maxAvatars = 5, size = "SM", enableDetailsModal, detailsModalHeader }: RenderUserTagGroupOptions,
): HTMLElement {
  ensureCss();

  const group = document.createElement("div");
  group.className = "cube-usertag-group";

  // MUI AvatarGroup: up to `max` shown; above that, max-1 avatars + surplus chip
  const clampedMax = Math.max(2, maxAvatars);
  const visible = ids.length > clampedMax ? ids.slice(0, clampedMax - 1) : ids;
  const surplus = ids.length - visible.length;

  const sizeRem = AVATAR_SIZE_REM[size];
  const fontSize = size === "XXS" || size === "XS" ? "0.5rem" : "1rem";

  visible.forEach((userId, index) => {
    const avatarEl = document.createElement("div");
    avatarEl.className = "cube-usertag-group__avatar";
    avatarEl.style.width = `${sizeRem}rem`;
    avatarEl.style.height = `${sizeRem}rem`;
    avatarEl.style.fontSize = fontSize;
    // Leftmost avatar stacks on top, like MUI AvatarGroup
    avatarEl.style.zIndex = String(visible.length - index);
    group.appendChild(avatarEl);

    if (!userId) return;
    const binding: TagBinding = {
      id: userId,
      kind: "groupAvatar",
      el: avatarEl,
      avatarEl,
      fallbackName: "",
      createdAt: Date.now(),
      painted: undefined,
    };
    const entry = store.getState().userTagData[userId];
    if (entry?.isLoaded) paint(binding, entry.data);
    else requestUserData(userId);
    register(binding);
  });

  if (surplus > 0) {
    const surplusEl = document.createElement("div");
    surplusEl.className = "cube-usertag-group__avatar";
    surplusEl.style.width = `${sizeRem}rem`;
    surplusEl.style.height = `${sizeRem}rem`;
    surplusEl.style.fontSize = "0.75rem";
    surplusEl.textContent = surplus >= 1000 ? `${String(surplus)[0]}k` : `+${surplus}`;
    group.appendChild(surplusEl);
  }

  if (enableDetailsModal && ids.length) {
    group.classList.add("cube-usertag-group--clickable");
    group.onclick = (e) => {
      e.stopPropagation();
      store.dispatch(openGroupedUsersModal({ ids: [...ids], header: detailsModalHeader }));
    };
  }

  container.appendChild(group);
  return container;
}

/**
 * Render every user as a full single-user tag (avatar + name), laid out in one
 * horizontal row into `container`. Unlike renderUserTagGroup (overlapping avatars
 * + "+N" surplus), each user reads at full detail like a standalone renderUserTag;
 * the row stays on a single line and clips at the cell edge when it overflows.
 * Each chip resolves/patches from the store and is independently clickable.
 */
export function renderUserTagList(
  container: HTMLElement,
  { users, enableDetailsModal, detailsModalHeader }: RenderUserTagListOptions,
): HTMLElement {
  ensureCss();

  if (!users.length) {
    container.textContent = "-";
    return container;
  }

  const list = document.createElement("div");
  list.className = "cube-usertag-list";

  // When the row overflows the cell the delegated gridCellTooltip (gridCellTooltip.ts) reads
  // `data-cube-tip` off the clipped element (or its nearest tipped ancestor). Without one it falls
  // back to textContent — every chip's name mashed together with its avatar initials
  // ("ASarun SharmaSJsiddharth jadhav"). The names to show usually do NOT come from `users`: grid
  // rows commonly carry assignee ids only and each chip resolves its own name from userTagData, so
  // the tip is (re)built from the chips' live labels via `onLabel` as that data lands.
  const labels = users.map((u) => u.fullName || u.email || "");
  // With the details modal the whole cell is one clickable unit, so every chip repeats the full
  // list — otherwise hovering a chip whose own name is ellipsized would tip that one name.
  const shareTipWithChips = Boolean(enableDetailsModal);

  const syncTip = (): void => {
    const tip = labels.filter(Boolean).join(", ");
    const targets: HTMLElement[] = [
      // The formatter's cell root too: when the box that clips sits ABOVE this row (the cell itself,
      // or a wrapper), the tooltip's upward walk starts there and would never see a tip left only on
      // the row — gridCellTooltip asks the cell's first element child as its last declared-tip stop.
      container,
      list,
      ...(shareTipWithChips ? Array.from(list.querySelectorAll<HTMLElement>(".cube-usertag")) : []),
    ];
    for (const el of targets) {
      if (tip) el.setAttribute("data-cube-tip", tip);
      else el.removeAttribute("data-cube-tip");
    }
  };

  // renderUserTag appends its chip to whatever element it is given, so each call
  // adds one chip to the shared row (reusing all of its resolve/paint/click logic).
  // With the details modal enabled the chips are non-interactive so their clicks
  // bubble up to the single row-level handler below (one modal for the whole cell).
  users.forEach((user, index) => {
    renderUserTag(list, {
      ...user,
      interactive: enableDetailsModal ? false : user.interactive,
      onLabel: (label) => {
        labels[index] = label;
        syncTip();
      },
    });
  });
  // Chips already resolved above painted before renderUserTag appended them, so this first full
  // sync is what actually stamps the row (and every chip in it) with the tooltip text.
  syncTip();

  if (enableDetailsModal) {
    const ids = users.map((u) => u.id).filter((id): id is ObjectIdString => Boolean(id));
    if (ids.length) {
      list.classList.add("cube-usertag-list--clickable");
      list.onclick = (e) => {
        e.stopPropagation();
        store.dispatch(openGroupedUsersModal({ ids, header: detailsModalHeader }));
      };
    }
  }

  container.appendChild(list);
  return container;
}

/** Diagnostics for the perf probe. */
export function __userTagDomStats() {
  let total = 0;
  let connected = 0;
  for (const set of bindingsById.values()) {
    for (const binding of set) {
      total++;
      if (binding.el.isConnected) connected++;
    }
  }
  return { users: bindingsById.size, total, connected, detached: total - connected };
}

declare global {
  interface Window {
    /** Debug-only hook, read by the perf probe from the console. */
    __userTagDomStats?: typeof __userTagDomStats;
  }
}

if (typeof window !== "undefined") {
  window.__userTagDomStats = __userTagDomStats;
}
